/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useMemo, useState, useEffect, useContext } from "react";
import { getLocale, useIntl, useSelector } from "umi";
import moment from "moment";
import PerformanceMetricsTable from "@/pages/dashboard/components/PerformanceMetricsTable";
import SelectShowValueType, { ShowValueType } from "@/pages/dashboard/pages/overview/components/OverviewTable/components/SelectShowValueType";
import MetricsSettings, { defaultStatisticsTypeValue } from "@/pages/dashboard/components/MetricsSettings";
import {
  useStatisticsDataFormat,
  useTableColumns,
  useTableDataFormat,
  useMetricsColumns
} from "./hooks";
import { LANGUAGE_MAP } from "@/pages/dashboard/_config/constant";
import DimensionAccumulateRow from "@/pages/dashboard/components/DimensionAccumulateRow";
import useData from "./useData";
import { MetricsSettingsProp, TableProps } from "./types";
import { useLocalText, useSelectArrObj } from "@/pages/dashboard/utils/customHook";
import { SortBy } from "tea-component/lib/table/addons";
import { PagingQuery } from "tea-component";
import { Option } from "@/pages/dashboard/components/DownloadFormatModal/types";
import { ComponentMetricsItemType } from "@/pages/dashboard/components/PageContainer/type";
import { downloadFileWithFileFormat, useNotificationOfDownloadTable } from "@/pages/dashboard/components/DownloadFormatModal";
import { textLocalConfig } from "@/pages/dashboard/pages/comparison/_config/constants";
import { FilterContext } from "@/pages/dashboard/components/PageContainer/context";
import { prepareMetricsDataSource, resolveAndSelectName, resolveConfigString } from "@/pages/dashboard/utils/metricUtils";
import { buildDimensionMetricsAndAppendToMap } from "@/pages/dashboard/utils/dimensionMetricsUtils";
import "@/pages/dashboard/assets/scss/page/Overlay/components/tableConext.scss";
import './index.scss';


export default function Table(props: TableProps) {
  const { config, style, data } = props || {};
  const propsData = props?.data;

  // 构建 metricsData(用于模板字符串解析)
  const metricsData = useMemo<Record<string, any>>(() => {
    return prepareMetricsDataSource(propsData?.metricsInfo);
  }, [propsData?.metricsInfo]);

  const { isHaveContrast = true,
    showStatistic: propsShowStatistic,
    dateDisabled = false,
    isShowSdlw = false,
    isShowDod = false,
    isShowChange = true,
    isShowMetricSetting=true,
    notHaveMetricsMap = false,
  } = config || {};

  const { dataFrame, metrics } = data || {};
  const { metric_key } = metrics || {};
  const locale = getLocale();
  const { formatMessage } = useIntl();
  const contextObj = useContext(FilterContext);
  const { filterVal, filterLoading } = contextObj || {};
  const { selector = {}, metric, pagePath, filterDefaultSelect } = filterVal || {};

  const { explainInfo, gameCode } = useSelector((state: any) => ({
    explainInfo: state.dashboard?.explainInfo,
    gameCode: state?.dashboard?.currentGame?.game_code,
  }));
  const [columns, setColumns] = useState<any[]>([]);
  const [selectType, setSelectType] = useState<ShowValueType>("value_only");
  const [showStatistic, setShowStatistic] = useState<string[]>(propsShowStatistic || defaultStatisticsTypeValue);
  const [accumulated, setAccumulated] = useState<string[]>(config?.dataKey === 'event_comparison'?[]:['date']);
  let SEPARATED_OPTIONS = useSelectArrObj(filterVal, false, false, filterDefaultSelect, pagePath, true);
  SEPARATED_OPTIONS = SEPARATED_OPTIONS?.filter((i: { groupKey: string; }) => i?.groupKey === 'separated');
  SEPARATED_OPTIONS.unshift({
    groupKey: 'separated',
    value: 'date',
    text: formatMessage({ id: 'common_by_Date' }),
    disabled: dateDisabled,
  });
  const compData = useData(filterVal, filterLoading, selectType, config, data);

  const {
    loading, isError, paging, setPaging, sort, setSort,
    total, tableData, statisticsData, filterValueSort, accumulateOldValueRef,
    getRequestParams, getData, downLoadData, dataKey, tableKey, statisticDate_timeType,
    frontLogicalFlag, overLimitBarTriggerKey
  } = compData || {};

  // 是否取消勾选date维度
  const hasDate = accumulateOldValueRef?.current?.includes('date');
  // 是否单日单周单月
  // const isSingle = IsSingleDay({ rangeTime: formValue?.contrastDate, period: PERIODTIMEOBJ[filterVal?.timeType] })
  const isSingle = false;
  // 是否累计值计算逻辑
  const isAccumulated = !hasDate && !isSingle;

  // 列定义, 中英文切换(全部), Accumulate关闭(仅仅维度列),
  const metricList = useMemo(() => {
    let filteredMetrics = config?.componentMetrics || [];

    if (dataKey === 'event_comparison') {
      const metrics = isAccumulated ? filterVal?.metrics?.filter((v: any) => v !== 'total_users')?.map((w: any) => {
        if (w === 'active_users') return 'avg_dau';
        return w;
      }) : filterVal?.metrics || [];

      filteredMetrics = config.componentMetrics.map((item: any) => {
        if (item?.metricKey === 'active_users' || item?.metricKey === 'avg_dau') {
          item = {
            ...item,
            metricKey: isAccumulated ? 'avg_dau' : 'active_users',
            reqMetricKey: isAccumulated ? 'avg_dau' : 'active_users',
            enName: isAccumulated ? 'Avg.DAU' : 'DAU',
            cnName: isAccumulated ? '日平均活跃' : '日活跃',
          };
        }
        return item;
      });

      filteredMetrics = filteredMetrics.filter((v: any) => metrics?.includes(v?.metricKey));
    } else if (dataKey === 'date_comparison') {
      const dateTypeMetric = config?.componentMetrics?.find((item: any) => item[statisticDate_timeType]);

      if (dateTypeMetric) {
        const metrics = dateTypeMetric[statisticDate_timeType].map((item: { metricKey: string;reqMetricKey:string;enName:string;cnName:string; }) => {
          if (item?.metricKey === 'active_users' || item?.metricKey === 'avg_dau') {
            let enName, cnName, metricKey;
            if (statisticDate_timeType === 'Week') {
              enName = isAccumulated ? 'Avg.WAU' : 'WAU';
              cnName = isAccumulated ? '周平均活跃' : '周活跃';
              metricKey= isAccumulated ? 'avg_wau' : 'active_users';
            }
            else if (statisticDate_timeType === 'Month') {
              enName = isAccumulated ? 'Avg.MAU' : 'MAU';
              cnName = isAccumulated ? '月平均活跃' : '月活跃';
              metricKey= isAccumulated ? 'avg_mau' : 'active_users';
            }
            else {
              enName = isAccumulated ? 'Avg.DAU' : 'DAU';
              cnName = isAccumulated ? '日平均活跃' : '日活跃';
              metricKey= isAccumulated ? 'avg_dau' : 'active_users';
            }
            item = {
              ...item,
              metricKey,
              reqMetricKey: metricKey,
              enName,
              cnName
            };
          }
          return item;
        });

        filteredMetrics = isAccumulated ? metrics.filter((v: any) => v.metricKey !== 'total_users') : metrics;
      } else {
        filteredMetrics = [];
      }
    }

    // 支持通过 data.reqMetrics 或 data.tableMetric 配置筛选需要展示的指标
    // 优先级：reqMetrics > tableMetric
    const rawReqMetrics = propsData?.reqMetrics;
    const resolvedReqMetrics = rawReqMetrics
      ? (Array.isArray(rawReqMetrics) ? rawReqMetrics : (rawReqMetrics[statisticDate_timeType] || rawReqMetrics['Day'] || []))
      : null;
    const tableMetricFilter = resolvedReqMetrics || propsData?.tableMetric;
    if (tableMetricFilter && tableMetricFilter.length > 0) {
      const metricsMap = new Map<string, any>();
      filteredMetrics.forEach((metric: any) => {
        const key = metric.reqMetricKey || metric.metricKey;
        metricsMap.set(key, metric);
      });
      // 取消分日期时，将过滤 key 中的 active_users 替换为对应的 avg 指标，与上方替换逻辑保持一致
      const avgMetricMap: Record<string, string> = { Day: 'avg_dau', Week: 'avg_wau', Month: 'avg_mau' };
      filteredMetrics = tableMetricFilter
        .map((key: string) => {
          if (isAccumulated && dataKey === 'date_comparison' && key === 'active_users') {
            return metricsMap.get(avgMetricMap[statisticDate_timeType] || 'avg_dau');
          }
          return metricsMap.get(key);
        })
        .filter((metric: any): metric is any => metric !== undefined);
    }

    return filteredMetrics;
  }, [filterVal, isAccumulated, dataKey, config, statisticDate_timeType, propsData?.tableMetric, propsData?.reqMetrics]);

  const COLUMN_CONFIG = useMetricsColumns(dataKey, statisticDate_timeType, metricList || []);

  const [showMetrics, setShowMetrics] = useState(Object.keys(COLUMN_CONFIG));
  useEffect(() => {
    if (tableKey === 'key_performance') {
      setShowMetrics([metric_key]);
      // 夜莺宽表目前不支持取消分日期，数据是错的，后续有需要可放开
      // if (isAccumulated && metric_key === "active_users") {
      //   setShowMetrics(Object.keys(COLUMN_CONFIG).filter(key => key === 'avg_dau'))
      // } else
      //   setShowMetrics(Object.keys(COLUMN_CONFIG).filter(key => key === metric_key))
    }
    else if (tableKey === 'retention') {
      if (['30', '60'].includes(metric)) {
        const metricNumber = parseInt(metric);
        const filteredKeys = Object.keys(COLUMN_CONFIG).filter(key => !(key[0] === "d" && parseInt(key.slice(1)) > metricNumber));
        setShowMetrics(filteredKeys);
      } else {
        setShowMetrics(Object.keys(COLUMN_CONFIG));
      }
    }
    else {
      setShowMetrics(Object.keys(COLUMN_CONFIG));
    }
  }, [JSON.stringify(COLUMN_CONFIG), metric_key, tableKey, metric, isAccumulated]);


  const { DATE_COLUMN, getAccumulateColumns, getMetricColumns } = useTableColumns(filterVal, isAccumulated, dataKey, dataFrame, metric_key, isHaveContrast, hasDate);


  useEffect(() => {
    const columns = [
      ...(DATE_COLUMN || []),
      ...getAccumulateColumns(accumulateOldValueRef?.current?.filter(v => v !== 'date'), gameCode, selector),
      ...getMetricColumns(showMetrics, COLUMN_CONFIG, explainInfo, selectType, metricsData)
    ];
    setColumns(columns);
  }, [accumulateOldValueRef?.current, selectType, filterVal, showMetrics, locale, metricList, metricsData]);

  // 表格数据
  const records = [
    ...useTableDataFormat(tableData, dataKey, COLUMN_CONFIG, filterVal),
    ...useStatisticsDataFormat(statisticsData, dataKey, COLUMN_CONFIG, filterVal, showStatistic, false)
  ];
  const copyData = [
    ...useTableDataFormat(tableData, dataKey, COLUMN_CONFIG, filterVal, true,),
    ...useStatisticsDataFormat(statisticsData, dataKey, COLUMN_CONFIG, filterVal, showStatistic, true)
  ];

  // 下载弹窗指标options
  const metricOptions: Option[] = useMemo(() => {
    let keys = Object.keys(COLUMN_CONFIG);

    if (tableKey === 'key_performance') {
      keys = keys.filter(key => key === metric_key);
    }
    if (tableKey === 'retention') {
      if (['30', '60'].includes(metric)) {
        const metricNumber = parseInt(metric);
        keys = keys.filter(key => !(key[0] === "d" && parseInt(key.slice(1)) > metricNumber));
      }
    }
    const isEn = locale === 'en-US';
    return keys.map((key) => {
      const config = COLUMN_CONFIG[key]?.metricsConfig;
      const name = resolveAndSelectName(isEn, config?.enName, config?.cnName, propsData?.metricsInfo) || '';
      return ({ value: key, text: name });
    });
  }, [COLUMN_CONFIG, tableKey, metric_key, locale, metric, propsData?.metricsInfo]);

  const onSortChange = (sorts: SortBy[] = []) => {
    // 排序
    setSort(sorts);
    // 发起请求
    const params = getRequestParams(filterValueSort?.current?.paging, sorts, filterValueSort?.current?.filterValue);
    // forceUpdate()
    getData(params);
  };

  const getExtractMetrics = (configMetrisArr: ComponentMetricsItemType[], metricsArr: any[]) => configMetrisArr.filter(item => metricsArr.includes(item.reqMetricKey));

  const localText = useLocalText(textLocalConfig);
  const { setNoticeStatus } = useNotificationOfDownloadTable(localText?.dateTitle);

  function downPromise() {
    const metricNameArr = getExtractMetrics(metricList, showMetrics);
    const metricsMap: { [key: string]: { en: string; zh: string; type: any; }; } = {};
    metricNameArr?.forEach(item => {
      const enName = resolveConfigString(item?.enName, propsData?.metricsInfo) || '';
      const cnName = resolveConfigString(item?.cnName, propsData?.metricsInfo) || '';
      metricsMap[item?.reqMetricKey] = {
        en: enName,
        zh: cnName,
        type: item?.type
      };
    });

    // 从 columns 提取维度信息，并把维度中英文补到 metricsMap
    const dimensionMetrics = buildDimensionMetricsAndAppendToMap(columns, metricsMap);

    const params = getRequestParams(paging, sort, filterValueSort?.current?.filterValue, {
      metrics: showMetrics,
      show_dod: selectType === "dod",
      show_sdlw: selectType === "sdlw",
      show_change: selectType === "change",
      aggregates: showStatistic,
      ...(!notHaveMetricsMap ? { metrics_map: metricsMap } : {}),
      ...(dimensionMetrics.length > 0 ? { dimension_metrics: dimensionMetrics } : {}),
    });
    const api = `${config?.api?.tableDownloadApi}?language=${LANGUAGE_MAP[locale]}`;
    return downLoadData(api, params);
  }
  // 下载
  function handleDownload() {
    const downloadPromise = downPromise?.();
    if (downloadPromise) { // 有返回Promise对象时由内部处理下载过程
      // 展示正在下载中通知
      setNoticeStatus("loading");
      // 发起请求下载
      (async () => {
        try {
          const res = await downloadPromise;
          const fileName = `${localText?.dateTitle.replace(/\s/g, '_')} ${moment().format('YYYY_MM_DD HH_mm_ss')}`;
          await downloadFileWithFileFormat(res, fileName, {
            onDownloadSuccess() {
              setNoticeStatus("success");
            },
            onDownloadError() {
              setNoticeStatus("error");
            }
          });
        } catch (e) {
          setNoticeStatus("error");
        }
      })();
    }
  }

  return (
    <div style={style || {}}>
      <PerformanceMetricsTable
        className={tableKey === 'retention' ? 'dbd-date-retention-tracking-table' : 'dbd-date-event-table'}
        title={localText?.dateTitle}
        columns={columns}
        records={records}
        copyConfig={
          {
            copyTitleList: dataKey === "date_comparison" ? ['copy date', 'current'] : [],
            copyData
          }
        }
        selectType={selectType}
        timeType={filterVal?.timeType}
        sorting={{
          defaultSorts: sort,
          onSortChange,
        }}
        paging={{
          pageIndex: paging.pageIndex,
          pageSize: paging.pageSize,
          recordCount: total,
          pageSizeOptions: [10, 20, 30, 40, 50, 100, 200],
          onPagingChange(query: PagingQuery) {
            // 切换分页
            setPaging(query);
            // 发起请求
            const params = getRequestParams(query, sort, filterValueSort?.current?.filterValue);
            getData(params);
          }
        }}
        topTip={{
          left: (<div className={`overall-conext-margin js-overview-table-dimension`}>
            <DimensionAccumulateRow
              options={SEPARATED_OPTIONS}
              value={accumulated}
              onChange={(value: string[]) => {
                setPaging({ pageSize: 10, pageIndex: 1 });
                setSort([]);
                setAccumulated(value);
                if (accumulateOldValueRef?.current?.toString() !== value?.toString()) {
                  accumulateOldValueRef.current = value;
                  const params = getRequestParams({ pageSize: 10, pageIndex: 1 }, [], filterValueSort?.current?.filterValue);
                  getData(params);
                }
              }}
            />
            <SelectShowValueType
              timeType={filterVal.timeType}
              value={selectType}
              onChange={(value) => { setSelectType(value as ShowValueType) }}
              isShowDod={isShowDod}
              isShowSdlw={isShowSdlw}
              isShowChange={isShowChange}
            />
          </div>),
          right: (isShowMetricSetting)&&(<MetricsSettings
            key={JSON.stringify(metricOptions)}
            onConfirm={({ metricValues, statisticsType }: MetricsSettingsProp) => {
              setShowStatistic(statisticsType);
              setShowMetrics(metricValues);
            }}
            // disabledOptions={[{ value: "date", text: useIntl().formatMessage({ id: "common_date" }) }]}
            metricOptions={metricOptions}
            // 事件对比不勾选分日期，隐藏自定义指标
            showTypeOptions={!(!hasDate && config?.dataKey === 'event_comparison')}
            requireItem={!hasDate}
          />)
        }}
        onResizeEnd={(columns) => setColumns(columns)}
        isShowProgressBar
        loading={loading}
        isError={isError}
        onDownload={handleDownload}
        frontLogicalFlag={frontLogicalFlag}
        limitCount={config?.tableConfig?.overLimitCount}
        barLimitCount={config?.tableConfig?.overLimitBarCount}
        barDownloadDisabled={config?.tableConfig?.barDownloadDisabled}
        overLimitBarTriggerKey={overLimitBarTriggerKey}
      />
    </div>
  );
}
